import { ProgressBar } from "@/app/_components/ContactSales/components/ProgressIndicator";
import { cva, VariantProps } from "class-variance-authority";
import { useCallback, useEffect, useRef, useState } from "react";

const layoutVariants = cva("tw-size-full tw-flex-1", {
  variants: {
    width: {
      sm: "tw-max-w-sm tw-mx-auto tw-p-4 sm:tw-p-8",
      md: "tw-max-w-md tw-mx-auto tw-p-4 sm:tw-p-8",
      lg: "tw-max-w-lg tw-mx-auto tw-p-8 sm:tw-p-8 tw-min-h-[550px] xl:tw-p-14",
      xl: "tw-max-w-xl tw-mx-auto tw-p-4 sm:tw-p-8",
      full: "tw-w-full tw-p-6 sm:tw-p-8",
    },
  },
  defaultVariants: {
    width: "sm",
  },
});

export type HeroAgentCardLayoutVariants = VariantProps<typeof layoutVariants>;

export interface HeroAgentCardLayoutProps extends VariantProps<typeof layoutVariants> {
  children: React.ReactNode;
  className?: string;
}

export function HeroAgentCardLayout({ children, width, className }: HeroAgentCardLayoutProps) {
  return <div className={layoutVariants({ width, className })}>{children}</div>;
}

export function CenteredDiscLayout({ children }: { children: React.ReactNode }) {
  return <div className='tw-mx-auto tw-mb-7 tw-size-44 sm:tw-size-72'>{children}</div>;
}

function calculateCooldown({
  attempts,
  minCooldownSeconds,
  backOffFactor,
  maxCooldownSeconds,
}: {
  attempts: number;
  minCooldownSeconds: number;
  backOffFactor: number;
  maxCooldownSeconds: number;
}) {
  if (attempts <= 0) return 0;
  return Math.min(minCooldownSeconds * Math.pow(backOffFactor, attempts), maxCooldownSeconds);
}

export function ResendButton({
  enabledContent,
  disabledContent,
  onResend,
  initialAttempts = 0,
  minCooldownSeconds = 10,
  maxAttempts = 5,
  backOffFactor = 2,
  maxCooldownSeconds = 600,
}: {
  enabledContent: ({ attempts, maxAttempts }: { attempts: number; maxAttempts: number }) => React.ReactNode;
  disabledContent: ({
    cooldown,
    attempts,
    maxAttempts,
  }: {
    cooldown: number;
    attempts: number;
    maxAttempts: number;
  }) => React.ReactNode;
  onResend: () => void;
  initialAttempts?: number;
  minCooldownSeconds?: number;
  maxAttempts?: number;
  backOffFactor?: number;
  maxCooldownSeconds?: number;
}) {
  const [attempts, setAttempts] = useState(initialAttempts);
  const initialCooldown = calculateCooldown({
    attempts: initialAttempts,
    minCooldownSeconds,
    backOffFactor,
    maxCooldownSeconds,
  });
  const [cooldownEndTime, setCooldownEndTime] = useState(() =>
    initialCooldown > 0 ? Date.now() + initialCooldown * 1000 : 0,
  );
  const [cooldown, setCooldown] = useState(initialCooldown);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const canResend = cooldown === 0 && attempts < maxAttempts;

  // Single effect to handle countdown timer
  useEffect(() => {
    if (cooldownEndTime === 0) return;

    const tick = () => {
      const left = cooldownEndTime - Date.now();
      if (left <= 0) {
        setCooldown(0);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        return;
      }
      setCooldown(Math.ceil(left / 1000));
    };

    // Run immediately to sync state
    tick();
    timerRef.current = setInterval(tick, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [cooldownEndTime]);

  const handleSend = useCallback(() => {
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    const newCooldown = calculateCooldown({
      attempts: newAttempts,
      minCooldownSeconds,
      backOffFactor,
      maxCooldownSeconds,
    });
    setCooldown(newCooldown);
    setCooldownEndTime(Date.now() + newCooldown * 1000);
    onResend();
  }, [attempts, minCooldownSeconds, backOffFactor, maxCooldownSeconds, onResend]);

  return (
    <button
      type='button'
      onClick={handleSend}
      disabled={!canResend}
      className='hover:tw-underline tw-cursor-pointer disabled:tw-cursor-not-allowed'
    >
      {canResend ? enabledContent({ attempts, maxAttempts }) : disabledContent({ cooldown, attempts, maxAttempts })}
    </button>
  );
}

export function ResizableProgressBar({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  return (
    <div className='tw-flex-1 tw-max-w-60 lg:tw-max-w-32 xl:tw-max-w-60 tw-hidden md:tw-block'>
      <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
    </div>
  );
}
